import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import { blobErrorMessage } from "../../../../services/v2/inventory.service";
import MultiSelect from "../../../../components/kits/multiSelect";
import { Empty, Loading, PER_PAGE_DEFAULT, Pager, apiError, saveBlob, ui, useCampus, useDebounced, useIdleLocked, usePerms } from "../kits/shared";
import Traslados from "./traslados";
import Conteos from "./conteos";

/* Tienda → Bodegas (sección "warehouses"): existencias por bodega, movimientos con foto y kardex. */

const SECTION = "warehouses";

interface StockRow {
	id: number;
	waCode: string | null;
	name: string;
	kind: string;
	sizeValue: string | null;
	barcode: string | null;
	unit: string | null;
	onHand: number;
	serialized?: boolean;
	inTransit: number;
	eta: string | null;
	byWarehouse: { warehouseId: number; code: string; name: string; countryCode: string; onHand: number; inTransit: number; eta: string | null }[];
	colorValue?: string | null;
	/** "Blanco · M": lo que distingue a una talla/color de su producto. */
	label?: string;
	/** Producto con tallas/colores: un renglón con el total y sus tallas adentro (cada una con su existencia y sus movimientos). */
	variants?: StockRow[];
}

const Bodegas: React.FC = () => {
	const { campusId } = useCampus();
	const idleLocked = useIdleLocked();
	const perms: any = usePerms(SECTION);
	const canMove = !!perms.create;
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const isSuperAdmin = currentUser?.currentRole === "superAdmin";
	const transferPerms: any = usePerms("transfers");
	const countPerms: any = usePerms("stockCounts");
	const [tab, setTab] = useState<string>("stock");
	const [exporting, setExporting] = useState<boolean>(false);
	const http = HttpManager.getInstance();

	const [catalogs, setCatalogs] = useState<any>(null);
	const [warehouses, setWarehouses] = useState<any[]>([]);
	const [warehouseIds, setWarehouseIds] = useState<string[]>([]);
	const [query, setQuery] = useState<string>("");
	const debouncedQuery = useDebounced(query);
	const [onlyWithStock, setOnlyWithStock] = useState<boolean>(false);
	const [rows, setRows] = useState<StockRow[]>([]);
	const [open, setOpen] = useState<number[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [lastPage, setLastPage] = useState<number>(1);
	const [page, setPage] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [moving, setMoving] = useState<StockRow | null>(null);
	const [kardex, setKardex] = useState<StockRow | null>(null);
	const [modalBusy, setModalBusy] = useState<boolean>(false);
	const [modalError, setModalError] = useState<string | null>(null);
	/** Fallas fuera de la tabla (catálogo de bodegas, Excel): se dicen con su motivo en vez de dejar la pantalla vacía. */
	const [notice, setNotice] = useState<string | null>(null);

	useEffect(() => {
		if (!campusId) return;
		http.inventoryService.catalogs(campusId).then((response) => {
			setCatalogs(response.data);
			setWarehouses(response.data.warehouses || []);
		}).catch((requestError) => setNotice(apiError(requestError, "No se pudieron cargar las bodegas.")));
	}, [campusId]);

	const exportExcel = async () => {
		setExporting(true);
		setNotice(null);
		try {
			const response = await http.stockOpsService.exportStock(campusId, { q: debouncedQuery.trim(), warehouseIds, onlyWithStock: onlyWithStock ? 1 : 0 });
			saveBlob(response.data, "existencias.xlsx", "Inventario");
		} catch (requestError) {
			setNotice(await blobErrorMessage(requestError, "No se pudo generar el Excel."));
		} finally {
			setExporting(false);
		}
	};

	const load = useCallback(async () => {
		if (!campusId) return;
		setLoading(true);
		setError(null);
		try {
			const response = await http.inventoryService.stock(campusId, {
				q: debouncedQuery.trim(), warehouseIds, onlyWithStock: onlyWithStock ? 1 : 0, grouped: 1
			}, page, PER_PAGE_DEFAULT);
			setRows(response.data.data || []);
			setTotal(response.data.total || 0);
			setLastPage(response.data.last_page || 1);
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudieron cargar las existencias."));
			setRows([]);
		} finally {
			setLoading(false);
		}
	}, [campusId, debouncedQuery, warehouseIds, onlyWithStock, page]);

	useEffect(() => { load(); }, [load]);

	const visibleWarehouses = warehouses.filter((warehouse) => warehouseIds.length < 1 || warehouseIds.includes(String(warehouse.id)));

	const saveMovement = async (data: Record<string, any>, photo: File | null) => {
		setModalBusy(true);
		setModalError(null);
		try {
			await http.inventoryService.movement(campusId, data, photo);
			setMoving(null);
			await load();
		} catch (requestError: any) {
			setModalError(apiError(requestError, "No se pudo registrar el movimiento."));
		} finally {
			setModalBusy(false);
		}
	};

	if (!campusId) return <div style={ui.page}>Selecciona un plantel.</div>;

	return <div style={ui.page}>
		<h2 style={ui.title}>Bodegas</h2>
		<div style={ui.tabs}>
			{[["stock", "Existencias"], ["transfers", "Traslados"], ["counts", "Conteos"]]
				.filter(([key]) => key === "stock" || (key === "transfers" ? transferPerms.read : countPerms.read))
				.map(([key, label]) => <button key={key} type="button" disabled={idleLocked} className={"btn rounded-pill " + (tab === key ? "btn-primary" : "btn-outline-secondary")} onClick={() => setTab(key)}>{label}</button>)}
		</div>
		{tab === "transfers" ? <Traslados campusId={campusId} warehouses={warehouses} perms={transferPerms} idleLocked={idleLocked} />
		: tab === "counts" ? <Conteos campusId={campusId} warehouses={warehouses} perms={countPerms} idleLocked={idleLocked} />
		: <>

		<div style={{ ...ui.card, ...ui.filters }}>
			<div style={ui.field("280px")}>
				<span style={ui.caption}>Buscar</span>
				<input type="text" className="form-control" style={ui.input} placeholder="Producto, código WA o escanear código de barras"
					value={query} disabled={idleLocked} onChange={(event) => { setQuery(event.target.value); setPage(1); }} />
			</div>
			<MultiSelect caption="Bodegas" width="240px" options={warehouses.map((warehouse) => ({ id: String(warehouse.id), name: warehouse.name, hint: warehouse.code }))}
				value={warehouseIds} disabled={idleLocked} onChange={(ids) => { setWarehouseIds(ids); setPage(1); }} />
			<div style={ui.field("180px")}>
				<span style={ui.caption}>Mostrar</span>
				<select className="form-select" style={ui.input} value={onlyWithStock ? "1" : "0"} disabled={idleLocked}
					onChange={(event) => { setOnlyWithStock(event.target.value === "1"); setPage(1); }}>
					<option value="0">Todos los productos</option>
					<option value="1">Solo con existencia</option>
				</select>
			</div>
		</div>

		<div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
			<button type="button" className="btn btn-outline-success btn-sm rounded-pill" disabled={idleLocked || exporting} onClick={exportExcel}>
				{exporting ? "Generando…" : "Exportar Excel"}</button>
		</div>
		{notice ? <div className="alert alert-danger" style={{ marginTop: "8px", padding: "8px 12px", fontSize: "14px", display: "flex", gap: "10px", alignItems: "flex-start" }}>
			<span style={{ flex: 1 }}>{notice}</span>
			<button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setNotice(null)} />
		</div> : null}
		<Pager page={page} lastPage={lastPage} total={total} unit="productos" busy={loading} onPage={setPage} />
		<div style={{ ...ui.card, overflowX: "auto" }}>
			{loading ? <Loading /> : error ? <div className="alert alert-danger" style={{ margin: "16px" }}>{error}</div>
				: rows.length < 1 ? <Empty text={debouncedQuery.trim() !== "" || onlyWithStock || warehouseIds.length > 0
					? "Sin resultados con estos filtros."
					: "Todavía no hay productos que se cuenten. El conteo inicial se hace en la pestaña Conteos."} />
				: <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr>
						<th style={ui.th}>Producto</th>
						<th style={ui.th}>Código</th>
						{visibleWarehouses.map((warehouse) => <th key={warehouse.id} style={{ ...ui.th, ...ui.right }} title={warehouse.name}>
							{warehouse.code}
							{/* En el celular no hay "title": el nombre y si entrega a alumnos van debajo del código. */}
							<div style={{ ...ui.tiny, whiteSpace: "normal", maxWidth: "110px", marginLeft: "auto", fontWeight: 400 }}>{warehouse.name}{warehouse.delivers === false ? " · no entrega" : ""}</div>
						</th>)}
						<th style={{ ...ui.th, ...ui.right }}>Total</th>
						<th style={ui.th}></th>
					</tr></thead>
					<tbody>{rows.map((product) => [product, ...(open.includes(product.id) ? (product.variants || []) : [])].map((row, position) => {
						const variant = position > 0;
						return <tr key={row.id} style={variant ? { background: "var(--wa-papel-2)" } : undefined}>
						<td style={{ ...ui.td, paddingLeft: variant ? "28px" : undefined }}>
							{variant
								? <div style={{ fontWeight: 600 }}>{row.label || row.sizeValue || row.name}</div>
								: <div style={{ fontWeight: 600 }}>{row.name}</div>}
							{!variant && row.variants && row.variants.length
								? <button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: "12px" }} onClick={() => setOpen(open.includes(row.id) ? open.filter((id) => id !== row.id) : [...open, row.id])}>
									{open.includes(row.id) ? "Ocultar Tallas" : "Ver " + row.variants.length + (row.variants.some((entry) => entry.colorValue) ? " Tallas y Colores" : " Tallas")}</button>
								: <div style={ui.tiny}>{!variant && row.sizeValue ? "Talla " + row.sizeValue + " · " : ""}{row.unit || "pieza"}</div>}
						</td>
						<td style={{ ...ui.td, whiteSpace: "nowrap", fontFamily: "monospace", fontSize: "12px" }}>{row.waCode}</td>
						{visibleWarehouses.map((warehouse) => {
							const cell = row.byWarehouse.find((entry) => entry.warehouseId === warehouse.id);
							const value = cell ? cell.onHand : 0;
							return <td key={warehouse.id} style={{ ...ui.td, ...ui.right, color: value > 0 ? "var(--wa-tinta)" : "var(--wa-filo-2)", fontWeight: value > 0 ? 600 : 400 }}>
								{value}
								{cell && cell.inTransit > 0 ? <div style={{ ...ui.tiny, color: "var(--wa-acento-texto)", fontWeight: 600 }} title={cell.eta ? "Llega " + cell.eta : undefined}>+{cell.inTransit} en camino</div> : null}
							</td>;
						})}
						<td style={{ ...ui.td, ...ui.right, fontWeight: 700 }}>
							{row.onHand}
							{row.inTransit > 0 ? <div style={{ ...ui.tiny, color: "var(--wa-acento-texto)", fontWeight: 600 }}>+{row.inTransit}{row.eta ? " · " + row.eta.slice(0, 5) : ""}</div> : null}
						</td>
						<td style={ui.td}>{row.variants && row.variants.length ? null : <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end", whiteSpace: "nowrap" }}>
							<button type="button" className={ui.btn} disabled={idleLocked} onClick={() => setKardex(row)}>Movimientos</button>
							{canMove ? <button type="button" className={ui.btnPrimary} disabled={idleLocked} onClick={() => { setModalError(null); setMoving(row); }}>Movimiento</button> : null}
						</div>}</td>
					</tr>;
					}))}</tbody>
				</table>}
		</div>
		{lastPage > 1 ? <Pager page={page} lastPage={lastPage} total={total} unit="productos" busy={loading} onPage={setPage} /> : null}

		{moving ? <MovementModal product={moving} catalogs={catalogs} canAddStock={isSuperAdmin} busy={modalBusy} error={modalError}
			onSave={saveMovement} onCancel={() => { if (!modalBusy) setMoving(null); }} /> : null}
		{kardex ? <KardexModal campusId={campusId} product={kardex} onClose={() => setKardex(null)} /> : null}
		</>}
	</div>;
};

/** Desde el 30-sep-2026 el inventario entra por Compras; sin orden, solo Super Admin registra entradas. */
const ENTRY_TYPES = ["opening", "return"];

const MovementModal: React.FC<{ product: any; catalogs: any; canAddStock: boolean; busy?: boolean; error?: string | null; onSave: (data: Record<string, any>, photo: File | null) => void; onCancel: () => void }> = ({ product, catalogs, canAddStock, busy, error, onSave, onCancel }) => {
	const types = (catalogs?.movementTypes || []).filter((option: any) => canAddStock || !ENTRY_TYPES.includes(option.id));
	const [warehouseId, setWarehouseId] = useState<string>(String((catalogs?.warehouses || [])[0]?.id || ""));
	const [type, setType] = useState<string>(canAddStock ? "opening" : "write_off");
	const [quantity, setQuantity] = useState<string>("1");
	const [reason, setReason] = useState<string>("");
	const [note, setNote] = useState<string>("");
	const [photo, setPhoto] = useState<File | null>(null);
	const [serials, setSerials] = useState<string>("");
	/* Ajuste por conteo: "Faltan" sale; "Sobran" entra y, desde el 30-sep-2026, solo Super Admin mete inventario sin orden. */
	const [direction, setDirection] = useState<"out" | "in">("out");

	const needsPhoto = type === "adjustment" || type === "write_off";
	const isNegative = type === "write_off" || type === "class_use" || (type === "adjustment" && direction === "out");
	const valid = warehouseId !== "" && Number(quantity) !== 0 && (!needsPhoto || (photo !== null && note.trim().length > 2));

	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
		<div className="card" style={{ width: "100%", maxWidth: "540px", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">
				<h4 style={{ marginBottom: "2px" }}>Movimiento de inventario</h4>
				<div style={{ ...ui.small, marginBottom: "12px" }}>{product.name}{product.sizeValue ? " · talla " + product.sizeValue : ""} · {product.waCode} · existencia total {product.onHand}</div>

				<div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
					<div style={ui.field("100%")}>
						<span style={ui.caption}>Bodega</span>
						<select className="form-select" style={ui.input} value={warehouseId} disabled={busy} onChange={(event) => setWarehouseId(event.target.value)}>
							{(catalogs?.warehouses || []).map((warehouse: any) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
						</select>
					</div>
					<div style={ui.field("48%")}>
						<span style={ui.caption}>Tipo</span>
						<select className="form-select" style={ui.input} value={type} disabled={busy} onChange={(event) => setType(event.target.value)}>
							{types.map((option: any) => <option key={option.id} value={option.id}>{option.name}</option>)}
						</select>
					</div>
					{type === "adjustment" ? <div style={ui.field("100%")}>
						<span style={ui.caption}>Qué encontraste al contar</span>
						<select className="form-select" style={ui.input} value={direction} disabled={busy} onChange={(event) => setDirection(event.target.value as "out" | "in")}>
							<option value="out">Faltan piezas (sale de la existencia)</option>
							{canAddStock ? <option value="in">Sobran piezas (entra a la existencia)</option> : null}
						</select>
					</div> : null}
					<div style={ui.field("48%")}>
						<span style={ui.caption}>Cantidad {isNegative ? "(sale)" : "(entra)"}</span>
						<input type="number" min={1} className="form-control" style={ui.input} value={quantity} disabled={busy} onChange={(event) => setQuantity(event.target.value)} />
					</div>
					{needsPhoto ? <div style={ui.field("100%")}>
						<span style={ui.caption}>Motivo</span>
						<select className="form-select" style={ui.input} value={reason} disabled={busy} onChange={(event) => setReason(event.target.value)}>
							<option value="">Elige un motivo…</option>
							{(catalogs?.movementReasons || []).map((option: any) => <option key={option.id} value={option.id}>{option.name}</option>)}
						</select>
					</div> : null}
					{product.serialized ? <div style={ui.field("100%")}>
						<span style={ui.caption}>Números de serie <span style={{ color: "var(--wa-oro)" }}>*</span></span>
						<input type="text" className="form-control" style={ui.input} placeholder="Separados por coma" value={serials} disabled={busy} onChange={(event) => setSerials(event.target.value)} />
					</div> : null}
					<div style={ui.field("100%")}>
						<span style={ui.caption}>Nota {needsPhoto ? <span style={{ color: "var(--wa-oro)" }}>*</span> : "(opcional)"}</span>
						<textarea className="form-control" rows={2} style={{ fontSize: "13px", resize: "vertical" }} value={note} disabled={busy}
							onChange={(event) => setNote(event.target.value)} placeholder="Qué pasó y quién lo autoriza" />
					</div>
					<div style={ui.field("100%")}>
						<span style={ui.caption}>Foto {needsPhoto ? <span style={{ color: "var(--wa-oro)" }}>*</span> : "(opcional)"}</span>
						<input type="file" accept="image/*" capture="environment" className="form-control" style={{ fontSize: "12px" }} disabled={busy}
							onChange={(event) => setPhoto(event.target.files && event.target.files[0] ? event.target.files[0] : null)} />
					</div>
				</div>

				{error ? <div className="alert alert-danger" style={{ marginTop: "12px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
				<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
					<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
					<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !valid}
						onClick={() => onSave({ warehouseId: Number(warehouseId), articleId: product.id, type,
							// The back signs write-offs and class use; an adjustment travels with its own sign.
							quantity: type === "adjustment" && direction === "out" ? -Math.abs(Number(quantity)) : Math.abs(Number(quantity)),
							reason: reason || undefined, note, serials: product.serialized ? serials : undefined }, photo)}>
						{busy ? "Guardando…" : "Registrar"}
					</button>
				</div>
			</div>
		</div>
	</div>;
};

const KARDEX_PAGE = 100;

const KardexModal: React.FC<{ campusId: number; product: any; onClose: () => void }> = ({ campusId, product, onClose }) => {
	const [rows, setRows] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [page, setPage] = useState<number>(1);
	const [lastPage, setLastPage] = useState<number>(1);
	const [total, setTotal] = useState<number>(0);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		let alive = true;
		setLoading(true);
		setError(null);
		HttpManager.getInstance().inventoryService.movements(campusId, product.id, null, page, KARDEX_PAGE)
			.then((response) => {
				if (!alive) return;
				// "Ver más" agrega la página siguiente debajo; antes solo se veían los primeros 200, sin aviso.
				setRows((previous) => page === 1 ? (response.data.data || []) : [...previous, ...(response.data.data || [])]);
				setLastPage(response.data.last_page || 1);
				setTotal(response.data.total || 0);
			})
			.catch((requestError) => { if (alive) setError(apiError(requestError, "No se pudieron cargar los movimientos.")); })
			.finally(() => { if (alive) setLoading(false); });
		return () => { alive = false; };
	}, [campusId, product.id, page]);

	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
		<div className="card" style={{ width: "100%", maxWidth: "760px", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">
				<div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
					<div style={{ flex: 1 }}>
						<h4 style={{ margin: 0 }}>Movimientos · {product.name}</h4>
						<div style={ui.small}>{product.waCode} · existencia total {product.onHand}</div>
					</div>
					<button type="button" className={ui.btn} onClick={onClose}>Cerrar</button>
				</div>
				<hr />
				{error ? <div className="alert alert-danger" style={{ padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
				{loading && rows.length < 1 ? <Loading /> : rows.length < 1 ? (error ? null : <Empty text="Sin movimientos registrados." />)
					: <table style={{ width: "100%", borderCollapse: "collapse" }}>
						<thead><tr>
							<th style={ui.th}>Fecha</th>
							<th style={ui.th}>Tipo</th>
							<th style={ui.th}>Bodega</th>
							<th style={{ ...ui.th, ...ui.right }}>Cantidad</th>
							<th style={{ ...ui.th, ...ui.right }}>Saldo</th>
							<th style={ui.th}>Quién</th>
							<th style={ui.th}>Nota</th>
						</tr></thead>
						<tbody>{rows.map((row) => <tr key={row.id}>
							<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{row.createdAt}</td>
							<td style={ui.td}>{row.typeLabel}{row.reasonLabel ? <div style={ui.tiny}>{row.reasonLabel}</div> : null}</td>
							<td style={ui.td}>{row.warehouse}</td>
							<td style={{ ...ui.td, ...ui.right, color: row.quantity < 0 ? "var(--wa-oro)" : "var(--wa-verde)", fontWeight: 600 }}>{row.quantity > 0 ? "+" : ""}{row.quantity}</td>
							<td style={{ ...ui.td, ...ui.right }}>{row.balanceAfter}</td>
							<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{row.author || "Sistema"}</td>
							<td style={ui.td}>{row.note || ""}{row.hasPhoto ? <div style={ui.tiny}>con foto</div> : null}</td>
						</tr>)}</tbody>
					</table>}
				{rows.length > 0 ? <div style={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "center", marginTop: "10px" }}>
					<span style={ui.small}>{rows.length} de {total} movimientos</span>
					{page < lastPage ? <button type="button" className={ui.btn} disabled={loading} onClick={() => setPage(page + 1)}>{loading ? "Cargando…" : "Ver Más"}</button> : null}
				</div> : null}
			</div>
		</div>
	</div>;
};

export default Bodegas;
